import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Modal,
  Stack,
  TextField,
} from "@mui/material";
import { Auth } from "aws-amplify";
import { ChangeEvent, useEffect, useState } from "react";

interface VerifyModalProps {
  open: boolean;
  title: string;
  subheader: string;
  attribute: string;
  isCodeSent: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 1,
  display: "flex",
  flexDirection: "column",
};

export const VerifyModal = (props: VerifyModalProps) => {
  const [code, setCode] = useState("");
  const [isSubmiting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(props.isCodeSent ? 60 : 0);
  const [closedown, setClosedown] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (props.open) {
      if (props.isCodeSent) {
        setCooldown(60);
      }
    }
  }, [props.open]);

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldown]);

  useEffect(() => {
    if (closedown > 0) {
      const timer = setTimeout(() => {
        setClosedown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (closedown === 0 && showSuccess) {
      setShowSuccess(false);
      props.onClose();
    }
  }, [closedown, showSuccess]);

  const handleVerify = async () => {
    setIsSubmitting(true);

    await Auth.verifyCurrentUserAttribute(props.attribute)
      .then(() => {
        setError("");
      })
      .catch((e) => {
        setError(e.message || "Something went wrong");
      })
      .finally(() => {
        setIsSubmitting(false);
        setCode("");
      });
  };

  const handleResend = async () => {
    await handleVerify();
    setCooldown(60);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await Auth.verifyCurrentUserAttributeSubmit(props.attribute, code)
      .then(() => {
        setClosedown(5);
        setCooldown(0);
        setShowSuccess(true);
        props.onSuccess();
      })
      .catch((e) => {
        setError(e.message || "Something went wrong");
      })
      .finally(() => {
        setIsSubmitting(false);
        setCode("");
      });
  };

  const handleClose = () => {
    setCode("");
    setError("");
    props.onClose();
  };

  const onCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const code = event.target.value;
    const validCode = code.replace(/[^0-9]/g, "");
    setCode(validCode);
  };

  return (
    <Modal open={props.open} onClose={handleClose}>
      <Card sx={style}>
        <CardHeader title={props.title} subheader={props.subheader} />
        <CardContent>
          {closedown > 0 && (
            <Alert severity="success" variant="outlined" sx={{ mb: 1 }}>
              Verification successful!
            </Alert>
          )}
          {!showSuccess && (
            <Stack direction="column" spacing={1}>
              {error && (
                <Alert severity="error" variant="outlined" sx={{ mb: 1 }}>
                  {error}
                </Alert>
              )}
              <Stack direction="row" spacing={1}>
                <TextField
                  type="text"
                  value={code}
                  size="small"
                  fullWidth
                  onChange={onCodeChange}
                  inputProps={{
                    maxLength: 6,
                  }}
                  onKeyDown={async (ev) => {
                    if (ev.key === "Enter" && code.length === 6)
                      await handleSubmit();
                  }}
                />
                <LoadingButton
                  loading={isSubmiting}
                  size="small"
                  variant="outlined"
                  color="success"
                  onClick={handleSubmit}
                  disabled={code.length !== 6 || closedown > 0}
                >
                  Verify
                </LoadingButton>
              </Stack>

              <Button
                variant="outlined"
                onClick={handleResend}
                disabled={cooldown > 0 || closedown > 0}
              >
                {cooldown > 0
                  ? `Resend in ${cooldown}s`
                  : "Send Verification Code"}
              </Button>
            </Stack>
          )}
        </CardContent>
        <CardActions sx={{ justifyContent: "flex-end" }}>
          <Stack direction="row" spacing={1}>
            <Button
              disabled={isSubmiting || closedown > 0}
              fullWidth
              variant="text"
              onClick={handleClose}
            >
              {closedown > 0 ? `Closing in ${closedown} seconds` : "Close"}
            </Button>
          </Stack>
        </CardActions>
      </Card>
    </Modal>
  );
};
